import { Outlet, useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { tokenRowFilters, usePreviewStore } from "@/lib/preview/previewStore";
import { computeLegacyFloor, resolveCapabilities } from "@/lib/authz/capabilities";
import { buildPreviewRole } from "@/lib/preview/buildPreviewRole";
import { RolePreviewBanner } from "@/components/PBanner/RolePreviewBanner";
import { PreviewReadOnlyDialog } from "@/components/PBanner/PreviewReadOnlyDialog";
import { PreviewEndedNotice } from "@/components/PBanner/PreviewEndedNotice";
import { warmCommonEventRoutes } from "@/components/EventSidebar/routePrefetch";
import { EventSidebar } from "@/components/EventSidebar/EventSidebar";
import { SAppBar } from "@/components/AppBar/SAppBar";
import { useEventLayoutEventQuery } from "@/lib/query/hooks/useEventLayoutEvent";
import { useEventLayoutRoleQuery } from "@/lib/query/hooks/useEventLayoutRole";
import { SError } from "@/components/Error/SError";
import type { EventRoleDTO } from "@/types/EventRoleDTO";
import { SidebarProvider } from "@/providers/SidebarProvider";
import { EventProvider } from "@/providers/EventProvider";
import { EventScopedAiAssistant } from "@/providers/AiAssistantProvider";
import { AiTemplateTools } from "@/components/AiAssistant/AiTemplateTools";
import { AiWebsiteTools } from "@/components/AiAssistant/AiWebsiteTools";
import { AiImportTools } from "@/components/AiAssistant/AiImportTools";
import { AiNavigationTools } from "@/components/AiAssistant/AiNavigationTools";
import { GuestDetailLauncherProvider } from "@/components/PGuestDetails/GuestDetailLauncher";
import { CapabilitiesProvider } from "@/providers/CapabilitiesProvider";
import { OnboardingProvider } from "@/providers/OnboardingProvider";
import { TenantBlockedProvider } from "@/contexts/TenantBlockedContext";
import { TenantBlockedBannerHost } from "@/components/PBanner/TenantBlockedBannerHost";
import { EventPlanExpiryBannerHost } from "@/components/PBanner/EventPlanExpiryBannerHost";
import { GuestTrashUndoBar } from "@/components/GuestTrashUndoBar/GuestTrashUndoBar";
import { GlobalSearch } from "@/components/PSearch/GlobalSearch";
import { POnboarding } from "@/components/POnboarding/POnboarding";
import type { Event } from "@/types/Event";
import type { Plugin } from "@/types/enums/Plugin";
import { InlineLoadingSpinner } from "@/components/LoadingSpinner/InlineLoadingSpinner";
import { getPref } from "@/lib/storage/userPrefs";
import { tenantBlockedStore } from "@/lib/auth/tenantBlockedStore";

function isServerErr(x: unknown): x is { isServerError: true; message: string } {
  return !!x && typeof x === "object" && "isServerError" in x && (x as { isServerError?: boolean }).isServerError === true;
}

// Stable sentinel passed to EventSidebar during the eventQ loading window.
// `event?.plugins ?? []` would produce a fresh array every render, which
// would either loop or churn useEventSidebar's plugin-sync effect.
const EMPTY_PLUGINS: Plugin[] = [];

/**
 * FOCUS routes: dense detail surfaces that get the sidebar's ~300px back.
 * The AppBar stays — the user keeps the event name, the setup guide and a way
 * out — so this is "focus", not the builders' full chrome-less shell.
 *
 * Matched on the path rather than moved to a top-level route on purpose: the
 * builders own separate paths because they are separate products, whereas the
 * offering board is a step INSIDE the programme flow and its back-chip
 * round-trip (?returnView / ?returnDay / ?returnHidden) plus every existing
 * deep link hang off this exact URL.
 */
const FOCUS_ROUTES = [/^\/event\/[^/]+\/programme\/offering\/[^/]+/];

export function EventLayoutShell() {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const [collapsed] = useState(() => getPref("collapsed") === "true");
  const { pathname } = useLocation();
  const focusMode = FOCUS_ROUTES.some((route) => route.test(pathname));

  // Idle-warm the most-likely-next event sub-route chunks. Covers users
  // who don't hover (touch / keyboard / fast clickers) where P1.1's
  // hover-prefetch can't help. Idempotent; browser dedupes the current
  // route's module.
  useEffect(() => {
    type WindowWithIdle = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const w = window as WindowWithIdle;
    if (!w.requestIdleCallback) return;
    const id = w.requestIdleCallback(() => warmCommonEventRoutes(), {
      timeout: 2000,
    });
    return () => w.cancelIdleCallback?.(id);
  }, []);

  // Shared with the onboarding panel's setup track (same key + fields = one
  // request, one cache entry) — see useEventLayoutEvent.
  const eventQ = useEventLayoutEventQuery(eventId);
  // Shared key + field list (see useEventLayoutRole) — PBackgroundJobs reads
  // the same entry, and the setup surfaces ask it whether this event's role has
  // landed yet.
  const roleQ = useEventLayoutRoleQuery(eventId, { keepPrevious: true });

  const event: Event | undefined =
    eventQ.data && !isServerErr(eventQ.data)
      ? (eventQ.data as Event)
      : undefined;
  // F1 (PR 6): event-role failures are soft-failures — fail-closed VIEWER
  // so the layout still mounts (collaborators tile / write affordances
  // hide themselves) instead of replacing everything with <SError />.
  // Same default during the loading phase so the sidebar can paint.
  const eventRole: EventRoleDTO =
    roleQ.data && !isServerErr(roleQ.data)
      ? (roleQ.data as EventRoleDTO)
      : ({ role: "" } as EventRoleDTO);

  // The sidebar menu is gated on plugins (eventQ) AND capabilities (roleQ).
  // Until BOTH have landed the fallbacks (EMPTY_PLUGINS + the `{role:""}` →
  // VIEWER coercion in CapabilitiesProvider) filter the menu down to the only
  // entry that survives with no plugin: Guests, with Guestlist + Requests
  // under it. That painted a plausible-looking menu that then grew the rest of
  // its entries a beat later. Skeleton rows instead — the sidebar shell, logo
  // and search still paint immediately.
  //
  // `roleQ.data !== undefined` counts the keepPreviousData placeholder as
  // known (same call the eventQ `event` above already makes), so switching
  // events keeps the previous menu painted rather than flashing a skeleton.
  // A thrown error on either query also resolves data-less → the skeleton
  // would never end, so an error releases the gate and the sidebar falls back
  // to whatever the fail-closed defaults allow (server-error responses don't
  // reach here: eventQ's is caught by the SError branch below).
  const sidebarLoading =
    (!event && !eventQ.isError) || (roleQ.data === undefined && !roleQ.isError);

  // ── "Preview as role" ──────────────────────────────────────────────────
  // While previewing, swap a synthesized EventRoleDTO (the previewed role's
  // FULL caps) into every gate so the chrome — sidebar, tabs, buttons, column
  // visibility — matches exactly what that role sees. The backend preview
  // token independently scopes the DATA (rows/fields) and blocks writes.
  const previewActive = usePreviewStore((s) => s.active);
  const previewTarget = usePreviewStore((s) => s.target);
  const previewEventId = usePreviewStore((s) => s.eventId);
  const stopPreview = usePreviewStore((s) => s.stopPreview);
  // Live-edited caps drive the chrome. Editing re-renders this selector → the
  // effectiveRole memo recomputes → every can() consumer reflects the change
  // instantly (no remount: the outletKey below deliberately excludes caps).
  const previewEffectiveCaps = usePreviewStore((s) => s.effectiveCaps);
  const previewEffectiveRowFilters = usePreviewStore((s) => s.effectiveRowFilters);
  // The rows the preview TOKEN applies — what the role actually gets. The raw
  // ones exist only so the editor can render a half-typed row; feeding them
  // into the capability context made the "your row filters" list in access
  // settings advertise a scope the server never enforced (and the save
  // dropped). B2-0: unchanged rows, the empty-wire guard and an override the
  // server did not apply all mean the token keeps the stored rows — so does
  // the chrome. `tokenRowFilters` returns a stored reference, never a fresh
  // array (zustand 5 has no equality: a new array per call loops, M4).
  const previewMintedRowFilters = usePreviewStore(tokenRowFilters);
  const previewEffectiveFieldAllowlist = usePreviewStore(
    (s) => s.effectiveFieldAllowlist,
  );

  const isPreviewing =
    previewActive && !!previewTarget && previewEventId === eventId;

  // Remount the entire page subtree when entering/leaving/switching preview.
  // Capabilities flip at that moment, and several event pages place their
  // capability gate AFTER data hooks (`if (!can(...)) return <SNoAccess/>` then
  // more useQuery/useState) — a mid-mount cap flip would change their hook
  // count and crash ("rendered fewer hooks than expected"). A key change forces
  // a clean unmount+remount so every page mounts fresh with consistent hooks.
  const outletKey = isPreviewing
    ? `${eventId}::preview::${previewTarget?.kind === "template" ? previewTarget.templateId : previewTarget?.baseRole}`
    : eventId;

  const effectiveRole: EventRoleDTO = useMemo(() => {
    if (!isPreviewing || !previewTarget) return eventRole;
    // Edited caps (falls back to the role's real caps when unedited).
    const caps =
      previewEffectiveCaps.length > 0
        ? previewEffectiveCaps
        : previewTarget.capabilities;
    return buildPreviewRole(
      eventRole,
      previewTarget,
      caps,
      previewEffectiveFieldAllowlist,
      previewMintedRowFilters,
    );
  }, [
    isPreviewing,
    previewTarget,
    previewEffectiveCaps,
    previewMintedRowFilters,
    previewEffectiveFieldAllowlist,
    eventRole,
  ]);

  // Event for the appbar — guarantee `eventId` on the object (the eventQ
  // projection omits it) so the EventSwitcher / recent-events tracking keep
  // working. Memoized so SAppBar's downstream effect doesn't re-run per render.
  const appbarEvent = useMemo(
    () => (event ? { ...event, eventId } : null),
    [event, eventId],
  );

  // The ADMIN's REAL capability to persist preview edits back to a template —
  // resolved from the logged-in role, NOT the previewed one (the banner renders
  // inside the preview CapabilitiesProvider, so a local can() would reflect the
  // role being edited and could be self-granted).
  const adminCanSaveTemplate = useMemo(
    () =>
      resolveCapabilities({
        role: eventRole.role,
        capabilities: eventRole.capabilities,
      }).has("event-role-templates:write"),
    [eventRole],
  );

  // Auto-stop preview when navigating to a DIFFERENT event (the shell instance
  // is reused across events, so a stale token must not leak) or when leaving
  // the event area entirely (unmount).
  useEffect(() => {
    if (previewActive && previewEventId && previewEventId !== eventId) {
      void stopPreview();
    }
  }, [previewActive, previewEventId, eventId, stopPreview]);
  useEffect(() => {
    return () => {
      if (usePreviewStore.getState().active) {
        void usePreviewStore.getState().stopPreview();
      }
    };
  }, []);

  // B2-0: a preview that ended LOCALLY (revoked/expired bearer) — a blocking
  // notice in a copy tab and after an unplanned end in every tab, a toast in
  // the minting tab after its expiry self-stop. ONE instance above BOTH
  // branches (PB-FB-H3-PREV-SHELLFATAL): the stop's cache clear drops this
  // shell's own event read, its refetch is refused by the request tombstone
  // and lands in the fatal branch below — which had no notice, so a blocking
  // end showed "Something went wrong" instead of "Preview ended — reload".
  // First sibling, before the subtree with the outlet: its keydown capture
  // registers before the as-admin remounted page's listeners (passive
  // effects run in sibling order), and a branch switch does not remount it.
  const withNotice = (tree: ReactNode) => (
    <>
      <PreviewEndedNotice host="shell" eventId={eventId || null} />
      {tree}
    </>
  );

  // Fatal-error gate: only fires once eventQ resolved with a server error.
  // Loading state intentionally falls through so the sidebar + AppBar can
  // paint immediately — only the page Outlet waits for event data.
  // W2-F (C3): this gate MUST live below every hook — it used to sit right
  // after the two queries, so the resolve-to-ServerError render skipped the
  // preview/memo/effect hooks above and React crashed with "Rendered fewer
  // hooks than expected" (the AppCrash boundary ate the whole shell instead
  // of showing SError/notFound).
  if (isServerErr(eventQ.data)) {
    return withNotice(
      // W2-F (C3): SAppBar consumes useOnboarding — without the provider
      // this fatal branch itself threw and the AppCrash boundary replaced
      // the intended SError screen.
      <OnboardingProvider initialProgress={null}>
        <div className="surface-secondary flex flex-col w-full h-dvh">
          <SAppBar />
          <SError error={eventQ.data.message} />
        </div>
      </OnboardingProvider>,
    );
  }

  return withNotice(
    <TenantBlockedProvider initialBlocked={tenantBlockedStore.isBlocked()} eventId={eventId}>
      <OnboardingProvider initialProgress={null}>
        <CapabilitiesProvider
          eventRole={effectiveRole}
          // Same "still loading" test the sidebar skeleton uses; a thrown
          // error releases it (the fail-closed VIEWER then stands).
          roleLoading={!isPreviewing && roleQ.data === undefined && !roleQ.isError}
        >
          <SidebarProvider>
            <div className="surface-secondary flex flex-row w-full h-dvh print:block print:h-auto print:w-auto transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none sm:pr-[var(--ai-rail-push)]">
              {/* Focus mode reclaims the DOCKED sidebar column, which only
                  exists from `lg` up — below that the sidebar is an off-canvas
                  drawer that costs no width and is the only navigation there.
                  Unmounting it outright left the AppBar's `lg:hidden` hamburger
                  toggling a drawer that no longer existed: it appeared to do
                  nothing, and the back chip became the only way out of the
                  page. So hide it at `lg` and up, keep it below. */}
              <div className={focusMode ? "no-print lg:hidden" : "no-print"}>
                {(
                  <EventSidebar
                    collapsed={collapsed}
                    plugins={event?.plugins ?? EMPTY_PLUGINS}
                    eventRole={effectiveRole.role}
                    isArchived={event?.archived ?? false}
                    isPlanLocked={event?.planLocked ?? false}
                    sidebarOrder={event?.sidebarOrder}
                    loading={sidebarLoading}
                  />
                )}
              </div>
              {/* Main column: the preview banner is a STATIC top bar ABOVE the
                  scroller so it never collides with the sticky appbar (both
                  were previously sticky-top-0 siblings inside the scroller →
                  the appbar painted over the banner on scroll). When preview is
                  inactive the banner renders null, so the DOM matches before. */}
              <div className="flex flex-col flex-1 min-w-0">
                <RolePreviewBanner canSaveToTemplate={adminCanSaveTemplate} />
                {/* Read-only notice shown when a write is attempted in preview
                    (portals to body; self-gates on the block store). */}
                <PreviewReadOnlyDialog />
                {/* B2-0: the PreviewEndedNotice sits above both branches
                    (`withNotice`). */}
                <div
                  data-testid="page-root"
                  className="flex flex-col flex-1 overflow-y-auto isolate"
                >
                  {/* Wrapper must be sticky ITSELF: SAppBar's own sticky can
                      only travel within its parent, and an appbar-height
                      wrapper gives it zero range — the bar scrolled away on
                      every overflowing event page. */}
                  <div className="no-print sticky top-0 z-40">
                    <SAppBar event={appbarEvent} />
                  </div>
                  {/* Inside the scroller, below the sticky bar — same rule as
                      DashboardShell and as EventPlanExpiryBannerHost further
                      down. It used to sit outside as a `fixed` overlay; in flow
                      out there it would have pushed the `h-dvh` row past the
                      viewport and clipped the sidebar. */}
                  <TenantBlockedBannerHost />
                  {/* `isPlaceholderData`: right after an event switch `event`
                      is still the PREVIOUS event (keepPreviousData). The
                      projection has no `eventId`, so this flag is the only
                      way a reader below can tell — AiImportTools must not
                      parse event B's sheet in event A's zone. */}
                  {event ? (
                    <EventProvider
                      event={event}
                      eventRole={effectiveRole}
                      eventIsPlaceholder={eventQ.isPlaceholderData}
                    >
                      {/* AI drawer renders INSIDE this provider (not as a
                          GlobalSearch sibling — that sits outside EventProvider
                          and context doesn't flow sideways). */}
                      <EventScopedAiAssistant>
                        {/* Template client tools are page-independent (route
                            eventId only) — registered globally so the AI can
                            create templates from any event page. */}
                        <AiTemplateTools />
                        <AiWebsiteTools />
                        <AiImportTools />
                        {/* Navigation client tools (navigate_to_page,
                            open_guest_profile) need an app-wide guest-detail
                            launcher so the AI can open any guest from any page. */}
                        <GuestDetailLauncherProvider eventId={eventId}>
                          <AiNavigationTools />
                          <EventPlanExpiryBannerHost />
                          {/* W6: the 15 s "moved to the trash · Undo" pill,
                              once per event so it outlives a page change. */}
                          <GuestTrashUndoBar eventId={eventId} />
                          <Outlet key={outletKey} />
                        </GuestDetailLauncherProvider>
                      </EventScopedAiAssistant>
                    </EventProvider>
                  ) : (
                    <InlineLoadingSpinner />
                  )}
                </div>
              </div>
            </div>
            <GlobalSearch />
            <POnboarding />
          </SidebarProvider>
        </CapabilitiesProvider>
      </OnboardingProvider>
    </TenantBlockedProvider>,
  );
}
